import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { motion } from "framer-motion";
import { EnterpriseSidebar } from "./EnterpriseDashboard";
import { Upload, FileText, CheckCircle, XCircle, Clock, AlertTriangle, Plus, Trash2, ListChecks, X } from "lucide-react";
import DistributeTab from "../components/DistributeTab";
import MVUploadTab from "../components/MVUploadTab";

const STATUS_STYLE = {
    pending: "border-gray-600 text-gray-400",
    parsing: "border-blue-500/40 text-blue-300",
    review_required: "border-amber-500/40 text-amber-300",
    approved: "border-emerald-500/40 text-emerald-300",
    rejected: "border-red-500/40 text-red-300",
};

const STATUS_ICON = {
    pending: Clock, parsing: Clock, review_required: AlertTriangle,
    approved: CheckCircle, rejected: XCircle,
};

export default function AssessmentsPage() {
    const [tab, setTab] = useState("distribute");
    return (
        <div className="flex bg-bg min-h-screen">
            <EnterpriseSidebar />
            <main className="ml-56 flex-1 p-8 min-h-screen" data-testid="assessments-page">
                <div className="mb-8">
                    <h1 className="text-3xl font-display font-bold text-white">Assessments</h1>
                    <p className="text-sm text-gray-500 mt-1">Generate, upload, approve, and distribute assessment instruments for this workspace.</p>
                </div>

                <div className="border-b border-border/40 mb-6 flex gap-1">
                    {[
                        { key: "generated", label: "Generated" },
                        { key: "uploaded", label: "Uploaded" },
                        { key: "import", label: "Import Questions" },
                        { key: "mv_upload", label: "MV Upload" },
                        { key: "library", label: "Library" },
                        { key: "distribute", label: "Assign & Distribute" },
                    ].map(t => (
                        <button key={t.key} onClick={() => setTab(t.key)}
                            className={`px-5 py-3 text-sm font-mono uppercase tracking-wider border-b-2 transition-colors ${
                                tab === t.key ? "border-white text-white" : "border-transparent text-gray-500 hover:text-gray-300"
                            }`}
                            data-testid={`assessments-tab-${t.key}`}>
                            {t.label}
                        </button>
                    ))}
                </div>

                {tab === "uploaded" && <UploadedTab />}
                {tab === "import" && <ImportQuestionsTab onSaved={() => setTab("distribute")} />}
                {tab === "mv_upload" && <MVUploadTab />}
                {tab === "distribute" && <DistributeTab />}
                {tab === "generated" && <PlaceholderTab label="Generated Assessments" hint="AI-generated from normalized JDs. Available via the Platform Admin → Assessment Generator surface." />}
                {tab === "library" && <PlaceholderTab label="Assessment Library" hint="All approved assessments — both generated and uploaded — appear here once approved." />}
            </main>
        </div>
    );
}


function PlaceholderTab({ label, hint }) {
    return (
        <div className="bg-card border border-border/40 p-12 text-center" data-testid="assessments-placeholder">
            <FileText className="w-8 h-8 text-gray-700 mx-auto mb-3" />
            <p className="text-sm text-gray-400 font-display">{label}</p>
            <p className="text-xs text-gray-600 mt-2 max-w-md mx-auto font-body">{hint}</p>
        </div>
    );
}


function UploadedTab() {
    const { api } = useAuth();
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [archetypes, setArchetypes] = useState([]);
    const [reviewing, setReviewing] = useState(null);

    const refresh = async () => {
        try {
            const r = await api.get("/v2/assessments/uploaded");
            setFiles(r.data.files || []);
        } catch (e) { /* no-op */ }
        finally { setLoading(false); }
    };

    useEffect(() => {
        refresh();
        api.get("/v2/admin/library/archetypes").then(r => setArchetypes(r.data.archetypes || [])).catch(() => {});
        const t = setInterval(refresh, 5000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="space-y-6">
            <UploadZone archetypes={archetypes} onUploaded={refresh} />

            <div className="bg-card border border-border/40" data-testid="uploaded-files-list">
                <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_120px] px-5 py-3 border-b border-border/40 text-xs text-gray-600 font-body uppercase tracking-wider">
                    <span>File</span><span>Mode</span><span>Status</span><span>Confidence</span><span>Uploaded</span><span></span>
                </div>
                {loading && <div className="px-5 py-6 text-xs text-gray-500">Loading…</div>}
                {!loading && files.length === 0 && (
                    <div className="px-5 py-8 text-xs text-gray-500 text-center">No uploads yet. Drop a file above to begin.</div>
                )}
                {files.map(f => {
                    const Icon = STATUS_ICON[f.parse_status] || Clock;
                    return (
                        <div key={f.id}
                            className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_120px] px-5 py-3 border-b border-border/20 items-center hover:bg-white/[0.01] transition-colors"
                            data-testid={`uploaded-row-${f.id}`}>
                            <div>
                                <span className="text-sm text-white font-mono text-xs truncate block">{f.file_name}</span>
                                {f.parse_status === "rejected" && f.parse_error && (
                                    <p className="text-[10px] text-red-300 mt-1 leading-snug" data-testid={`reject-reason-${f.id}`}>
                                        ⚠ {f.parse_error}
                                    </p>
                                )}
                            </div>
                            <span className="text-xs text-gray-400 font-mono">{f.upload_mode === "per_role" ? "Per Role" : "Per Candidate"}</span>
                            <span>
                                <span className={`text-xs font-mono uppercase tracking-wider px-2 py-0.5 border ${STATUS_STYLE[f.parse_status] || STATUS_STYLE.pending}`}>
                                    <Icon className="w-3 h-3 inline mr-1 -mt-0.5" />
                                    {f.parse_status?.replace("_", " ")}
                                </span>
                            </span>
                            <span className="text-xs text-gray-400 font-mono">{f.parse_confidence != null ? `${f.parse_confidence.toFixed(0)}%` : "—"}</span>
                            <span className="text-xs text-gray-600 font-mono">{f.uploaded_at?.slice(0, 10)}</span>
                            <span>
                                {(f.parse_status === "review_required" || f.parse_status === "approved") && (
                                    <button onClick={() => setReviewing(f.id)}
                                        className="text-xs px-2.5 py-1 border border-border/40 text-gray-300 hover:text-white hover:border-white/40"
                                        data-testid={`review-btn-${f.id}`}>
                                        Review
                                    </button>
                                )}
                            </span>
                        </div>
                    );
                })}
            </div>

            {reviewing && <ReviewModal fileId={reviewing} onClose={() => { setReviewing(null); refresh(); }} />}
        </div>
    );
}


function UploadZone({ archetypes, onUploaded }) {
    const { api } = useAuth();
    const inputRef = useRef(null);
    const [mode, setMode] = useState("per_role");
    const [archetypeId, setArchetypeId] = useState("");
    const [cadre, setCadre] = useState("Senior IC");
    const [candidateEmail, setCandidateEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const handleFile = async (file) => {
        if (!file) return;
        if (mode === "per_role" && !archetypeId) { setError("Select a role archetype first."); return; }
        if (mode === "per_candidate" && !candidateEmail) { setError("Enter a candidate email first."); return; }
        setError(null); setBusy(true);
        const fd = new FormData();
        fd.append("file", file);
        fd.append("upload_mode", mode);
        if (mode === "per_role") {
            fd.append("archetype_id", archetypeId);
            fd.append("cadre", cadre);
        } else {
            fd.append("candidate_email", candidateEmail);
        }
        try {
            await api.post("/v2/assessments/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            onUploaded();
            if (inputRef.current) inputRef.current.value = "";
        } catch (e) {
            setError(e.response?.data?.detail || "Upload failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="bg-card border border-border/40 p-6" data-testid="upload-zone">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h3 className="text-sm font-display font-bold text-white">Upload an assessment</h3>
                    <p className="text-xs text-gray-500 mt-0.5 font-body">.docx · .pdf · .md · .txt · .json · .html — up to 10 MB</p>
                </div>
                <div className="flex border border-border/40">
                    {["per_role", "per_candidate"].map(m => (
                        <button key={m} onClick={() => setMode(m)}
                            className={`text-xs font-mono uppercase tracking-wider px-3 py-1.5 ${
                                mode === m ? "bg-white text-black" : "text-gray-400 hover:text-white"
                            }`}
                            data-testid={`mode-${m}`}>
                            {m === "per_role" ? "Per Role" : "Per Candidate"}
                        </button>
                    ))}
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {mode === "per_role" ? (
                    <>
                        <div>
                            <label className="text-xs text-gray-500 block mb-1">Role archetype</label>
                            <select value={archetypeId} onChange={e => setArchetypeId(e.target.value)}
                                className="w-full bg-muted border border-border/40 px-3 py-2 text-sm text-white"
                                data-testid="archetype-select">
                                <option value="">Select…</option>
                                {archetypes.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-xs text-gray-500 block mb-1">Cadre</label>
                            <select value={cadre} onChange={e => setCadre(e.target.value)}
                                className="w-full bg-muted border border-border/40 px-3 py-2 text-sm text-white">
                                {["Junior", "Senior IC", "Architect-track", "Manager-track", "Executive"].map(c => <option key={c}>{c}</option>)}
                            </select>
                        </div>
                    </>
                ) : (
                    <div className="md:col-span-2">
                        <label className="text-xs text-gray-500 block mb-1">Candidate email</label>
                        <input value={candidateEmail} onChange={e => setCandidateEmail(e.target.value)}
                            placeholder="sarah.mensah@meridianfinancial.com"
                            className="w-full bg-muted border border-border/40 px-3 py-2 text-sm text-white font-mono"
                            data-testid="candidate-email-input" />
                        <p className="text-xs text-gray-600 mt-1">Mode B uploads must contain BOTH the assessment instrument AND the candidate's responses. Use only for legacy data ingestion.</p>
                    </div>
                )}
            </div>
            <div className="border border-dashed border-border/60 p-6 text-center hover:border-border transition-colors">
                <Upload className="w-6 h-6 text-gray-600 mx-auto mb-2" />
                <input ref={inputRef} type="file" className="hidden" id="upload-input"
                    accept=".docx,.pdf,.md,.txt,.json,.html"
                    onChange={e => handleFile(e.target.files?.[0])}
                    data-testid="upload-input" />
                <label htmlFor="upload-input"
                    className={`text-sm cursor-pointer ${busy ? "text-gray-600" : "text-gray-300 hover:text-white"}`}>
                    {busy ? "Uploading…" : "Click to choose a file"}
                </label>
            </div>
            {error && <p className="text-xs text-red-300 mt-3" data-testid="upload-error">{error}</p>}
        </div>
    );
}


function ReviewModal({ fileId, onClose }) {
    const { api } = useAuth();
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const [showRejectField, setShowRejectField] = useState(false);

    useEffect(() => {
        api.get(`/v2/assessments/uploaded/${fileId}/parse-result`).then(r => setData(r.data));
    }, [api, fileId]);

    const approve = async () => {
        setBusy(true);
        try { await api.post(`/v2/assessments/uploaded/${fileId}/approve`); onClose(); }
        catch (e) { alert(e.response?.data?.detail || "Approve failed"); }
        finally { setBusy(false); }
    };
    const reject = async () => {
        if (!rejectReason.trim()) return;
        setBusy(true);
        try { await api.post(`/v2/assessments/uploaded/${fileId}/reject`, { reason: rejectReason }); onClose(); }
        catch (e) { alert(e.response?.data?.detail || "Reject failed"); }
        finally { setBusy(false); }
    };

    if (!data) return null;
    const po = data.parse_output || {};
    const mods = po.modules || [];

    return (
        <div className="fixed inset-0 bg-black/80 flex items-start justify-center z-50 overflow-y-auto p-6" onClick={onClose}>
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                className="bg-card border border-border/40 w-full max-w-6xl my-12" onClick={e => e.stopPropagation()}
                data-testid="review-modal">
                <div className="p-6 border-b border-border/40 flex items-center justify-between">
                    <div>
                        <h2 className="text-lg font-display font-bold text-white">{data.file_name}</h2>
                        <p className="text-xs text-gray-500 mt-0.5 font-mono">
                            {data.upload_mode === "per_role" ? "Per Role" : "Per Candidate"} ·
                            status: <span className={`px-1.5 ${STATUS_STYLE[data.parse_status]}`}>{data.parse_status}</span> ·
                            confidence: {data.parse_confidence?.toFixed(0)}%
                        </p>
                    </div>
                    <button onClick={onClose} className="text-gray-500 hover:text-white text-xl">×</button>
                </div>
                {po.warnings?.length > 0 && (
                    <div className="bg-amber-500/5 border-b border-amber-500/30 px-6 py-3">
                        <p className="text-xs uppercase tracking-wider text-amber-300 mb-1">Warnings</p>
                        <ul className="text-xs text-amber-200 list-disc pl-5">
                            {po.warnings.map((w, i) => <li key={i}>{w}</li>)}
                        </ul>
                    </div>
                )}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 max-h-[70vh] overflow-hidden">
                    <div className="border-r border-border/40 p-6 overflow-y-auto" data-testid="review-original">
                        <p className="text-xs uppercase tracking-wider text-gray-500 mb-3">Original Upload</p>
                        <pre className="text-xs text-gray-300 whitespace-pre-wrap font-mono leading-relaxed">{data.raw_text || "(no text extracted)"}</pre>
                    </div>
                    <div className="p-6 overflow-y-auto" data-testid="review-parsed">
                        <p className="text-xs uppercase tracking-wider text-gray-500 mb-3">Canonical Mapping</p>
                        <p className="text-xs text-gray-400 mb-3 font-body">
                            Language: <span className="text-white font-mono">{po.language || "—"}</span> ·
                            Rubric: <span className="text-white font-mono">{po.scoring_rubric?.detected_scale || "—"}</span>
                        </p>
                        <div className="space-y-2">
                            {mods.map(m => (
                                <div key={m.module_key} className="border border-border/40 p-3" data-testid={`module-${m.module_key}`}>
                                    <div className="flex items-baseline justify-between">
                                        <span className="text-sm text-white font-display">{m.module_name}</span>
                                        <span className="text-xs font-mono text-emerald-300">{m.confidence}%</span>
                                    </div>
                                    <p className="text-xs text-gray-500 font-mono mt-1">{(m.questions || []).length} questions</p>
                                </div>
                            ))}
                            {mods.length === 0 && <p className="text-xs text-gray-500">No canonical modules detected.</p>}
                        </div>
                    </div>
                </div>
                <div className="p-6 border-t border-border/40 flex items-center justify-between gap-3">
                    <p className="text-xs text-gray-500">{po.notes || ""}</p>
                    <div className="flex gap-2">
                        {data.parse_status === "review_required" && (
                            <>
                                <button onClick={approve} disabled={busy}
                                    className="bg-white text-black px-4 py-2 text-sm font-semibold hover:bg-gray-200 disabled:opacity-40"
                                    data-testid="approve-btn">
                                    {busy ? "Approving…" : "Approve & Add to Library"}
                                </button>
                                <button onClick={() => setShowRejectField(true)}
                                    className="border border-red-500/40 text-red-300 px-4 py-2 text-sm hover:bg-red-500/10"
                                    data-testid="reject-btn-open">
                                    Reject
                                </button>
                            </>
                        )}
                        {data.parse_status === "approved" && (
                            <span className="text-xs text-emerald-300 font-mono">Approved · written to assessment_templates</span>
                        )}
                    </div>
                </div>
                {showRejectField && (
                    <div className="px-6 pb-6 border-t border-border/40 pt-4">
                        <label className="text-xs text-gray-500 block mb-1">Rejection reason *</label>
                        <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                            rows={2}
                            className="w-full bg-muted border border-border/40 px-3 py-2 text-sm text-white"
                            data-testid="reject-reason" />
                        <button onClick={reject} disabled={busy || !rejectReason.trim()}
                            className="mt-2 border border-red-500/40 text-red-300 px-4 py-2 text-sm hover:bg-red-500/10 disabled:opacity-40"
                            data-testid="reject-btn-confirm">
                            Confirm reject
                        </button>
                    </div>
                )}
            </motion.div>
        </div>
    );
}



const QTYPE_LABELS = {
    text: "Free text",
    likert_5: "Likert (1–5)",
    single_choice: "Single choice",
    multi_select: "Multi-select",
    percentage_allocation: "Percentage allocation (sum to 100)",
};

function ImportQuestionsTab({ onSaved }) {
    const { api, user } = useAuth();
    const inputRef = useRef(null);
    const [stage, setStage] = useState("upload"); // upload | extracting | review | saved
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [roleLabel, setRoleLabel] = useState("");
    const [cadre, setCadre] = useState("ic");
    const [savedTemplateId, setSavedTemplateId] = useState(null);
    const [savedCount, setSavedCount] = useState(0);
    const [extractionProgress, setExtractionProgress] = useState("");
    const pollRef = useRef(null);

    const wsId = user?.enterprise_id;

    const reset = () => {
        setStage("upload"); setError(null); setQuestions([]); setSavedTemplateId(null); setSavedCount(0);
        setExtractionProgress("");
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        if (inputRef.current) inputRef.current.value = "";
    };

    const handleFile = async (file) => {
        if (!file) return;
        setError(null); setBusy(true);
        if (!roleLabel) setRoleLabel(file.name.replace(/\.[^.]+$/, ""));
        setStage("extracting");
        setExtractionProgress("Reading your document…");
        const fd = new FormData();
        fd.append("file", file);
        try {
            const r = await api.post("/v2/assessments/question-import/extract", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const taskId = r.data.task_id;
            if (!taskId) throw new Error("No task id returned");
            startPolling(taskId);
        } catch (e) {
            setError(e.response?.data?.detail || "Could not start extraction.");
            setBusy(false);
            setStage("upload");
        }
    };

    const startPolling = (taskId) => {
        if (pollRef.current) clearInterval(pollRef.current);
        const startedAt = Date.now();
        let attempts = 0;
        const tick = async () => {
            attempts += 1;
            const elapsed = Math.floor((Date.now() - startedAt) / 1000);
            if (elapsed > 360) {
                clearInterval(pollRef.current); pollRef.current = null;
                setError("Extraction is taking longer than expected. Please try a smaller document.");
                setBusy(false); setStage("upload");
                return;
            }
            if (attempts === 4) setExtractionProgress("AI is identifying your questions…");
            if (attempts === 12) setExtractionProgress("Almost there — large documents take a bit longer…");
            try {
                const s = await api.get(`/v2/assessments/question-import/extract/status/${taskId}`);
                if (s.data.status === "completed") {
                    clearInterval(pollRef.current); pollRef.current = null;
                    setQuestions(s.data.questions || []);
                    setStage("review");
                    setBusy(false);
                } else if (s.data.status === "failed") {
                    clearInterval(pollRef.current); pollRef.current = null;
                    setError(s.data.error || "Extraction failed.");
                    setBusy(false); setStage("upload");
                }
            } catch (e) {
                // Transient network blip — keep polling unless 404 (task gone)
                if (e.response?.status === 404) {
                    clearInterval(pollRef.current); pollRef.current = null;
                    setError("Extraction task expired. Please re-upload.");
                    setBusy(false); setStage("upload");
                }
            }
        };
        pollRef.current = setInterval(tick, 3000);
        tick();
    };

    useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

    const updateQuestion = (i, patch) => {
        setQuestions(qs => qs.map((q, idx) => (idx === i ? { ...q, ...patch } : q)));
    };
    const updateOption = (qi, oi, val) => {
        setQuestions(qs => qs.map((q, idx) => {
            if (idx !== qi) return q;
            const opts = [...(q.options || [])]; opts[oi] = val;
            return { ...q, options: opts };
        }));
    };
    const addOption = (qi) => updateQuestion(qi, { options: [...(questions[qi].options || []), ""] });
    const removeOption = (qi, oi) => {
        const opts = [...(questions[qi].options || [])]; opts.splice(oi, 1);
        updateQuestion(qi, { options: opts });
    };
    const removeQuestion = (i) => setQuestions(qs => qs.filter((_, idx) => idx !== i));
    const addQuestion = () =>
        setQuestions(qs => [...qs, { id: `q${qs.length + 1}`, prompt: "", type: "text", options: [] }]);

    const onTypeChange = (i, newType) => {
        const cur = questions[i];
        const needsOptions = newType === "single_choice" || newType === "multi_select" || newType === "percentage_allocation";
        const opts = needsOptions
            ? (cur.options && cur.options.length >= 2 ? cur.options : ["", ""])
            : [];
        updateQuestion(i, { type: newType, options: opts });
    };

    const save = async () => {
        if (!roleLabel.trim()) { setError("Please enter a role label / assessment name."); return; }
        const cleaned = questions
            .map(q => ({
                ...q,
                prompt: (q.prompt || "").trim(),
                options: (q.options || []).map(o => (o || "").trim()).filter(Boolean),
            }))
            .filter(q => q.prompt);
        if (!cleaned.length) { setError("At least one question with a prompt is required."); return; }
        for (const q of cleaned) {
            if ((q.type === "single_choice" || q.type === "multi_select" || q.type === "percentage_allocation") && q.options.length < 2) {
                setError(`Question "${q.prompt.slice(0, 40)}…" needs at least 2 options for ${q.type}.`);
                return;
            }
        }
        setError(null); setBusy(true);
        try {
            const r = await api.post("/v2/assessments/question-import/save", {
                enterprise_id: wsId,
                role_label: roleLabel.trim(),
                cadre,
                questions: cleaned,
            });
            setSavedTemplateId(r.data.template_id);
            setSavedCount(r.data.question_count);
            setStage("saved");
        } catch (e) {
            setError(e.response?.data?.detail || "Save failed.");
        } finally {
            setBusy(false);
        }
    };

    if (stage === "saved") {
        return (
            <div className="bg-card border border-border/40 p-10 text-center" data-testid="import-saved">
                <CheckCircle className="w-10 h-10 text-emerald-300 mx-auto mb-3" />
                <h3 className="text-lg font-display font-bold text-white">Assessment ready</h3>
                <p className="text-sm text-gray-400 mt-2">
                    Saved <span className="text-emerald-300 font-mono">{savedCount}</span> question{savedCount === 1 ? "" : "s"} as
                    "<span className="text-white">{roleLabel}</span>".
                </p>
                <p className="text-xs text-gray-600 mt-1 font-mono">template id: {savedTemplateId}</p>
                <div className="flex gap-3 justify-center mt-6">
                    <button onClick={() => onSaved && onSaved()}
                        className="bg-white text-black px-5 py-2 text-sm font-mono uppercase tracking-wider hover:bg-gray-200"
                        data-testid="open-in-distribute-btn">
                        Open in Distribute
                    </button>
                    <button onClick={reset}
                        className="border border-border/40 text-gray-300 px-5 py-2 text-sm font-mono uppercase tracking-wider hover:text-white"
                        data-testid="import-another-btn">
                        Import another
                    </button>
                </div>
            </div>
        );
    }

    if (stage === "upload") {
        return (
            <div className="space-y-6" data-testid="import-upload-stage">
                <div className="bg-card border border-border/40 p-6">
                    <div className="flex items-start gap-3 mb-4">
                        <ListChecks className="w-5 h-5 text-gray-500 mt-0.5" />
                        <div>
                            <h3 className="text-sm font-display font-bold text-white">Import a custom question set</h3>
                            <p className="text-xs text-gray-500 mt-1 font-body leading-relaxed max-w-2xl">
                                Upload a document containing your own assessment questions (mix of free-text, Likert, or multiple choice).
                                Realloc will extract them verbatim — no canonical module mapping. Free-text answers are graded automatically
                                with Realloc's standard 0–5 anchored rubric.
                            </p>
                            <p className="text-xs text-gray-600 mt-2 font-body">.docx · .pdf · .md · .txt · .html — up to 10 MB</p>
                        </div>
                    </div>
                    <div className="border border-dashed border-border/60 p-8 text-center hover:border-border transition-colors">
                        <Upload className="w-7 h-7 text-gray-600 mx-auto mb-3" />
                        <input ref={inputRef} type="file" className="hidden" id="import-input"
                            accept=".docx,.pdf,.md,.txt,.html"
                            onChange={e => handleFile(e.target.files?.[0])}
                            data-testid="import-file-input" />
                        <label htmlFor="import-input"
                            className={`text-sm cursor-pointer ${busy ? "text-gray-600" : "text-gray-300 hover:text-white"}`}>
                            {busy ? "Extracting questions…" : "Click to choose a file"}
                        </label>
                        <p className="text-xs text-gray-600 mt-2 font-body">Realloc will read each question and let you edit them before saving.</p>
                    </div>
                    {error && <p className="text-xs text-red-300 mt-4" data-testid="import-error">{error}</p>}
                </div>
            </div>
        );
    }

    if (stage === "extracting") {
        return (
            <div className="bg-card border border-border/40 p-12 text-center" data-testid="import-extracting-stage">
                <div className="inline-block animate-pulse mb-4">
                    <div className="w-12 h-12 border-2 border-emerald-300/40 border-t-emerald-300 rounded-full animate-spin mx-auto" />
                </div>
                <h3 className="text-base font-display font-bold text-white">Extracting questions</h3>
                <p className="text-sm text-gray-400 mt-2">{extractionProgress || "Reading your document…"}</p>
                <p className="text-xs text-gray-600 mt-4 font-body max-w-md mx-auto">
                    This can take 30 seconds to a few minutes for longer documents. You can leave this tab open — we'll show your questions as soon as they're ready.
                </p>
                <button onClick={() => { reset(); }}
                    className="mt-6 text-xs text-gray-500 hover:text-white underline-offset-2 hover:underline"
                    data-testid="import-cancel-btn">
                    Cancel and start over
                </button>
                {error && <p className="text-xs text-red-300 mt-4" data-testid="import-error">{error}</p>}
            </div>
        );
    }

    // review stage
    return (
        <div className="space-y-5" data-testid="import-review-stage">
            <div className="bg-card border border-border/40 p-5">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                    <div className="md:col-span-2">
                        <label className="text-xs text-gray-500 block mb-1 uppercase tracking-wider font-mono">Assessment name</label>
                        <input value={roleLabel} onChange={e => setRoleLabel(e.target.value)}
                            placeholder="e.g. Senior Recruiter — H1 2026"
                            className="w-full bg-muted border border-border/40 px-3 py-2 text-sm text-white"
                            data-testid="role-label-input" />
                    </div>
                    <div>
                        <label className="text-xs text-gray-500 block mb-1 uppercase tracking-wider font-mono">Cadre</label>
                        <select value={cadre} onChange={e => setCadre(e.target.value)}
                            className="w-full bg-muted border border-border/40 px-3 py-2 text-sm text-white"
                            data-testid="cadre-select">
                            {[
                                ["apprentice", "Apprentice"],
                                ["practitioner_ic", "Practitioner-IC"],
                                ["ic", "IC"],
                                ["senior_ic", "Senior IC"],
                                ["architect_track", "Architect-track"],
                                ["executive_track", "Executive-track"],
                            ].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                    </div>
                </div>
                <div className="flex items-center justify-between">
                    <p className="text-xs text-gray-500 font-body">
                        <span className="text-emerald-300 font-mono">{questions.length}</span> question{questions.length === 1 ? "" : "s"} extracted.
                        Edit prompts, types, and options below.
                    </p>
                    <button onClick={reset}
                        className="text-xs text-gray-500 hover:text-white underline-offset-2 hover:underline"
                        data-testid="import-restart-btn">
                        Start over
                    </button>
                </div>
            </div>

            <div className="space-y-3">
                {questions.map((q, i) => (
                    <div key={i} className="bg-card border border-border/40 p-5" data-testid={`import-question-${i}`}>
                        <div className="flex items-start gap-3 mb-3">
                            <span className="text-xs text-gray-600 font-mono mt-2 w-6">Q{i + 1}</span>
                            <textarea value={q.prompt} onChange={e => updateQuestion(i, { prompt: e.target.value })}
                                rows={2}
                                className="flex-1 bg-muted border border-border/40 px-3 py-2 text-sm text-white resize-none"
                                placeholder="Question prompt…"
                                data-testid={`import-question-prompt-${i}`} />
                            <button onClick={() => removeQuestion(i)}
                                className="text-gray-600 hover:text-red-300 mt-2"
                                title="Remove question"
                                data-testid={`import-question-remove-${i}`}>
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="ml-9 flex items-center gap-2 mb-3">
                            <span className="text-xs text-gray-500 uppercase tracking-wider font-mono">Type</span>
                            <select value={q.type} onChange={e => onTypeChange(i, e.target.value)}
                                className="bg-muted border border-border/40 px-2 py-1 text-xs text-white"
                                data-testid={`import-question-type-${i}`}>
                                {Object.entries(QTYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                            </select>
                        </div>
                        {(q.type === "single_choice" || q.type === "multi_select" || q.type === "percentage_allocation") && (
                            <div className="ml-9 space-y-2">
                                {q.type === "percentage_allocation" && (
                                    <p className="text-xs text-gray-500 font-body mb-1">
                                        Each row is one bucket the candidate will allocate a % to. The total must sum to 100%.
                                    </p>
                                )}
                                {(q.options || []).map((opt, oi) => (
                                    <div key={oi} className="flex items-center gap-2">
                                        <span className="text-xs text-gray-600 font-mono w-6">
                                            {q.type === "percentage_allocation" ? `${oi + 1}.` : String.fromCharCode(65 + oi)}
                                        </span>
                                        <input value={opt} onChange={e => updateOption(i, oi, e.target.value)}
                                            placeholder={q.type === "percentage_allocation" ? `Bucket ${oi + 1} (e.g. Hands-on coding)` : `Option ${oi + 1}`}
                                            className="flex-1 bg-muted border border-border/40 px-3 py-1.5 text-xs text-white" />
                                        {q.type === "percentage_allocation" && (
                                            <span className="text-xs text-gray-600 font-mono">%</span>
                                        )}
                                        <button onClick={() => removeOption(i, oi)}
                                            className="text-gray-600 hover:text-red-300">
                                            <X className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                ))}
                                <button onClick={() => addOption(i)}
                                    className="text-xs text-gray-500 hover:text-white flex items-center gap-1.5 ml-8">
                                    <Plus className="w-3 h-3" /> {q.type === "percentage_allocation" ? "Add bucket" : "Add option"}
                                </button>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            <button onClick={addQuestion}
                className="w-full border border-dashed border-border/60 text-gray-500 hover:text-white hover:border-border py-3 text-xs font-mono uppercase tracking-wider"
                data-testid="add-question-btn">
                + Add a question
            </button>

            {error && <p className="text-xs text-red-300" data-testid="import-error">{error}</p>}

            <div className="flex justify-end gap-3 sticky bottom-4">
                <button onClick={save} disabled={busy}
                    className="bg-white text-black px-6 py-2.5 text-sm font-mono uppercase tracking-wider hover:bg-gray-200 disabled:opacity-40"
                    data-testid="import-save-btn">
                    {busy ? "Saving…" : "Save & make distributable"}
                </button>
            </div>
        </div>
    );
}
